import { Injectable, inject, signal, type ViewContainerRef } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { accusativeFullName, pluralRu } from '@lite-workspace/shared/format';
import { surchargePair, type FC_PhotoBookDTO, type UpsaleBillDto, type UpsaleSettings } from '@lite-workspace/dto-fotoclass-book';
import { formatMoneyRub } from '@lite-workspace/dto-fotoclass-studio';
import { ConfirmDialogComponent, HlmDialogOpener, type ConfirmDialogContext } from '@lite-workspace/shared/helm-ui/dialog';
import { ToastService } from '@lite-workspace/shared/helm-ui/toast';
import { AlbumGenerationTrackerService } from '../../../../core/album-generation-tracker.service';
import type { Person } from '../entities/project.models';
import { PersonsApiService } from '../infrastructure/persons-api.service';
import { albumProductId } from './product-geometry-refresh.service';
import { ProjectsFacade } from './projects.facade';

const FAILED = 'Почему-то не удалось.. попробуйте снова';
const TO_FOLDER = ['В папке будут обложка и разворот с виньеткой класса.', 'Отобранные групповые фото в неё не попадут.'];
// Person carries no gender: one neutral line for every student.
const TO_ALBUM = 'Откроется отбор групповых фото.';
const TO_ALBUM_MANY = 'Им откроется отбор групповых фото.';
/** Accusative after a count: «1 ученика», «3 ученика», «12 учеников». */
const STUDENTS = ['ученика', 'ученика', 'учеников'] as const;
const APPROVED_NOTICE = {
  title: 'Альбом уже утверждён.',
  text: 'Утверждение снимется, после обновления отправьте его на утверждение заново.',
};
const PAID_NOTICE = { title: 'Альбом уже оплачен.', text: 'Разницу в цене верните родителям сами.' };

const LOCK = 'Запретить ученику менять изделие';
const LOCK_MANY = 'Запретить ученикам менять изделие';
const NO_SURCHARGE = 'none';

/** Radios of the surcharge question; null price = several prices in the set, the label names none. */
function surchargeChoices(price: number | null) {
  const label = price === null ? 'Выставить доплату' : `Выставить доплату ${formatMoneyRub(price)}`;
  return {
    value: 'surcharge',
    options: [
      { value: 'surcharge', label, check: true },
      { value: NO_SURCHARGE, label: 'Без доплаты', check: false },
    ],
  };
}

export interface ItemMoveTarget {
  printProductId: string;
  folder: boolean;
}

/** The student's side of a single move: his current item and his class bill (null = none / not loaded). */
export interface ItemMoveFrom {
  printProductId?: string;
  bill?: UpsaleBillDto | null;
}

/**
 * The student's side of a move, one rule for the albums page and the editor: his item (his album row's product,
 * else the project `main`) and his bill among the class `bills`.
 */
export function studentMoveFrom(
  personId: string,
  albums: readonly FC_PhotoBookDTO[],
  main: string | undefined,
  bills: readonly UpsaleBillDto[],
): ItemMoveFrom {
  const album = albums.find(row => row.personId === personId);
  return {
    printProductId: (album && albumProductId(album)) ?? main,
    bill: bills.find(row => row.personId === personId) ?? null,
  };
}

/**
 * Price of the photographer's surcharge for this move, null = no radios (a free pair has nothing to bill);
 * the rule = shared `surchargePair`.
 */
export function surchargePrice(
  settings: UpsaleSettings | null | undefined,
  from: string | undefined,
  to: string,
  bill: UpsaleBillDto | null | undefined,
): number | null {
  const pair = surchargePair(settings, from, to, bill?.positions ?? []);
  return pair && pair.price !== 'free' ? pair.price : null;
}

/** One student onto another project item: confirm → approval revoked first → move → albums re-read. */
@Injectable({ providedIn: 'root' })
export class ItemMoveFlowService {
  private readonly facade = inject(ProjectsFacade);
  private readonly dialog = inject(HlmDialogOpener);
  private readonly toast = inject(ToastService);
  private readonly api = inject(PersonsApiService);
  private readonly tracker = inject(AlbumGenerationTrackerService);
  /** Album id → its `generatedAt` before the move: until a newer build lands, its old fresh previews are not «done». */
  private readonly rebuilds = signal<ReadonlyMap<string, string | undefined>>(new Map());

  /** Reactive: the album still shows the build from before its item move. */
  awaitsRebuild(album: FC_PhotoBookDTO): boolean {
    const rebuilds = this.rebuilds();
    return rebuilds.has(album.id) && rebuilds.get(album.id) === album.generationInfo?.generatedAt;
  }

  // The server keeps the row status while the gateway rebuilds: the tracker veils the card until the rebuilt
  // album's previews land; its TTL bounds a rebuild that never comes.
  private markRebuilt(projectId: string, result: { albumId?: string | null; state?: string }): void {
    const albumId = result.albumId;
    if (!albumId || result.state !== 'generating') return;
    const before = this.facade.albums().find(album => album.id === albumId);
    if (before) this.rebuilds.update(map => new Map(map).set(albumId, before.generationInfo?.generatedAt));
    this.tracker.markActive(projectId, albumId);
  }

  /** Resolves true once the server moved the student; cancel, a refused revoke or a failed move = false. */
  async move(
    projectId: string,
    person: Person,
    target: ItemMoveTarget,
    vcr?: ViewContainerRef,
    from: ItemMoveFrom = {},
  ): Promise<boolean> {
    const album = this.facade.albums().find(row => row.personId === person.id);
    const approved = album?.status === 'approved';
    const kind = target.folder ? 'папку' : 'альбом';
    // Teachers are never billed and pick no item themselves: no radios, no lock.
    const student = person.role !== 'teacher';
    const price = student
      ? surchargePrice(this.facade.upsaleByProject()[projectId], from.printProductId, target.printProductId, from.bill)
      : null;
    // A paid upgrade stays on the bill after the move: the photographer refunds the difference himself.
    const paid = (from.bill?.positions ?? []).some(
      p => p.kind === 'upsize' && p.paid && p.content.printProductId !== target.printProductId,
    );
    const notice = paid ? PAID_NOTICE : approved ? APPROVED_NOTICE : undefined;
    let touched = false;
    let moved = false;
    const ref = this.dialog.open<ConfirmDialogContext, boolean>(ConfirmDialogComponent, vcr, {
      title: `Перевести ${accusativeFullName(`${person.firstName} ${person.surname}`.trim())} на ${kind}?`,
      message: target.folder ? TO_FOLDER : TO_ALBUM,
      ...(notice ? { notice } : {}),
      ...(price === null ? {} : { choices: surchargeChoices(price) }),
      ...(student ? { checkbox: { label: LOCK, checked: price !== null } } : {}),
      confirmLabel: `Перевести на ${kind}`,
      onConfirm: async (lock, choice) => {
        touched = true;
        try {
          if (approved && album) {
            const { rejected } = await this.facade.revokeApproval(projectId, [album.id]);
            if (rejected.length > 0) {
              this.toast.error(FAILED);
              return;
            }
          }

          const result = await this.facade.moveItem(projectId, person.id, {
            printProductId: target.printProductId,
            ...(price === null ? {} : { surcharge: choice !== NO_SURCHARGE }),
            ...(student ? { lock } : {}),
          });
          this.markRebuilt(projectId, result);
          moved = true;
        } catch {
          this.toast.error(FAILED);
        }
      },
    });
    await firstValueFrom(ref.closed$);
    // A failed move may still have revoked the approval: the list re-reads whenever a write was tried.
    if (touched) await this.facade.loadAlbums(projectId).catch(() => undefined);
    return moved;
  }

  /** Lock-only write: the student keeps his item, the pin comes off; no rebuild, the list re-reads the flag. */
  async unlock(projectId: string, personId: string, printProductId: string): Promise<boolean> {
    try {
      await this.facade.moveItem(projectId, personId, { printProductId, lock: false });
    } catch {
      this.toast.error(FAILED);
      return false;
    }

    await this.facade.loadAlbums(projectId).catch(() => undefined);
    return true;
  }

  /**
   * Bulk twin of `move`: one window, approvals revoked first, one request; resolves the person ids the server moved.
   * `froms` = students only (teachers left out): radios once any of them qualifies, the server bills those alone.
   * `nameOf` names the students the server did not move in the failure toast.
   */
  async moveMany(
    projectId: string,
    personIds: readonly string[],
    target: ItemMoveTarget,
    vcr?: ViewContainerRef,
    froms: ReadonlyMap<string, ItemMoveFrom> = new Map(),
    nameOf: (personId: string) => string | undefined = () => undefined,
  ): Promise<string[]> {
    const ids = new Set(personIds);
    const approved = this.facade
      .albums()
      .filter(row => row.personId !== null && ids.has(row.personId) && row.status === 'approved');
    const kind = target.folder ? 'папку' : 'альбом';
    const n = personIds.length;
    const settings = this.facade.upsaleByProject()[projectId];
    const prices = new Set(
      personIds.flatMap(id => {
        const from = froms.get(id);
        const price = from ? surchargePrice(settings, from.printProductId, target.printProductId, from.bill) : null;
        return price === null ? [] : [price];
      }),
    );
    const qualifies = prices.size > 0;
    let touched = false;
    let moved: string[] = [];
    const ref = this.dialog.open<ConfirmDialogContext, boolean>(ConfirmDialogComponent, vcr, {
      title: `Перевести ${n} ${pluralRu(n, STUDENTS)} на ${kind}?`,
      message: target.folder ? TO_FOLDER : TO_ALBUM_MANY,
      ...(approved.length > 0 ? { notice: { title: approvedCountLine(approved.length), text: '' } } : {}),
      ...(qualifies ? { choices: surchargeChoices(prices.size === 1 ? [...prices][0] : null) } : {}),
      checkbox: { label: LOCK_MANY, checked: qualifies },
      confirmLabel: `Перевести на ${kind}`,
      onConfirm: async (lock, choice) => {
        touched = true;
        try {
          if (approved.length > 0) {
            const { rejected } = await this.facade.revokeApproval(
              projectId,
              approved.map(row => row.id),
            );
            if (rejected.length > 0) {
              this.toast.error(FAILED);
              return;
            }
          }

          const body = {
            personIds: [...personIds],
            printProductId: target.printProductId,
            ...(qualifies ? { surcharge: choice !== NO_SURCHARGE } : {}),
            lock,
          };
          const results = await firstValueFrom(this.api.moveItems(projectId, body));
          for (const row of results) if (!row.error) this.markRebuilt(projectId, row);
          moved = results.filter(row => !row.error).map(row => row.personId);
          const failed = results.filter(row => !!row.error).map(row => row.personId);
          if (failed.length > 0) this.toast.error(notMovedLine(failed, target.folder, nameOf));
          if (moved.length > 0) this.toast.success(`Перевели ${moved.length} ${pluralRu(moved.length, STUDENTS)} на ${kind}`);
        } catch {
          this.toast.error(FAILED);
        }
      },
    });
    await firstValueFrom(ref.closed$);
    if (touched) await this.facade.loadAlbums(projectId).catch(() => undefined);
    return moved;
  }
}

/** Partial batch: the count and names of the students left behind, then what to do (they stay selected). */
function notMovedLine(failed: readonly string[], folder: boolean, nameOf: (personId: string) => string | undefined): string {
  const n = failed.length;
  const names = failed.flatMap(id => nameOf(id) ?? []).join(', ');
  const head = `${n} ${pluralRu(n, ['ученик не переведён', 'ученика не переведены', 'учеников не переведены'])}`;
  return `${head} на ${folder ? 'папку' : 'альбом'}${names ? `: ${names}` : ''}.. попробуйте повторить действие`;
}

function approvedCountLine(n: number): string {
  return `${n} ${pluralRu(n, ['альбом утверждён', 'альбома утверждены', 'альбомов утверждены'])} — утверждение снимется.`;
}
